/*
 * Copyright (c) 2018 Network International.
 * The copyright notice above does not evidence any
 * actual or intended publication of such source code.
 */

import get from 'lodash/get';
import includes from 'lodash/includes';

const retrieveQueryParams = () => {
  const [, queryParams] = document.location.href.split('?');
  const params = new URLSearchParams(queryParams);
  const code = params.get('code');
  const threeDSStatus = params.get('3ds_status');
  let slim = 0;
  if (params.get('slim') === 'true' || params.get('slim') === '1') {
    slim = 1;
  } else if (params.get('slim') === '2') {
    slim = 2;
  }
  const from3ds = params.get('from3ds') === 'true';
  const showBasket = params.get('showBasket') === 'true';
  const paymentId = params.get('payment_id');
  const paymentMethod = params.get('payment_method');
  const threeDSCompInd = params.get('threeDSCompInd');
  const base64EncodedCRes = params.get('base64EncodedCRes');
  const invoiceRef = params.get('invoiceRef');
  const darkTheme = params.get('darkTheme');

  return {
    slim,
    showBasket,
    code,
    threeDSStatus,
    from3ds,
    paymentId,
    paymentMethod,
    threeDSCompInd,
    invoiceRef,
    base64EncodedCRes,
    darkTheme
  };
};


const setInStorage = (cname, cvalue) => {
  if (typeof (Storage) === 'undefined') {
    const d = new Date();
    d.setTime(d.getTime() + (10 * 1000 * 60));
    const expires = `expires=${d.toGMTString()}`;
    window.document.cookie = `${cname}=${cvalue}; ${expires}`;
  } else {
    sessionStorage.setItem(cname, cvalue);
  }
};

const setInLocalStorage = (cname, cvalue) => {
  if (typeof (Storage) === 'undefined') {
    const d = new Date();
    d.setTime(d.getTime() + (10 * 1000 * 60));
    const expires = `expires=${d.toGMTString()}`;
    window.document.cookie = `${cname}=${cvalue}; ${expires}`;
  } else {
    localStorage.setItem(cname, cvalue);
  }
};

const checkIfStorageIsAvailable = () => {
  try {
    if (window.sessionStorage.length >= 0) {
      return true;
    }
  } catch (err) {
    return false;
  }
  return false;
};

const getFromStorage = (name) => {
  if (typeof (Storage) === 'undefined') {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    if (parts.length === 2) {
      return parts
        .pop()
        .split(';')
        .shift();
    }
    return '';
  }
  return sessionStorage.getItem(name);
};

const appendQueryParams = (currentUrl, params) => {
  let paramsStr = '';
  Object.entries(params).forEach(([key, value]) => {
    if (paramsStr) {
      paramsStr += '&';
    }
    paramsStr += `${key}=${value}`;
  });

  if (currentUrl.indexOf('?') >= 0) {
    return `${currentUrl}&${paramsStr}`;
  }

  return `${currentUrl}?${paramsStr}`;
};

const getResponseCode = (state) => {
  const successStates = ['CAPTURED', 'AUTHORISED', 'PURCHASED', 'VERIFIED', 'PARTIALLY_AUTHORISED'];
  return successStates.filter(currId => state === currId).length > 0 ? '00' : '13';
};

const isSavedCard = (orderDetails) => {
  const {order = {merchantAttributes: {}}} = orderDetails;
  const {
    addSavedCardQueryParams = false
  } = order.merchantAttributes;
  return addSavedCardQueryParams;
}

const getRedirectToMerchantUrl = (orderDetails) => {
  const { order = { merchantAttributes: {} } } = orderDetails;
  const {
    redirectUrl = '',
    addSavedCardQueryParams = false
  } = order.merchantAttributes;


  const queryParams = { ref: order.reference };

  if (addSavedCardQueryParams) {
    queryParams.paymentToken = order.reference;
    queryParams.responseCode = getResponseCode(orderDetails.state);
  }

  return appendQueryParams(redirectUrl, queryParams);
};

const getCanceUrl = (orderDetails) => {
  const cancelUrl = get(orderDetails, 'order.merchantAttributes.cancelUrl', '');
  const addSavedCardQueryParams = get(orderDetails, 'order.merchantAttributes.addSavedCardQueryParams', false);
  const reference = get(orderDetails, 'order.reference', '');

  const queryParams = { ref: reference };

  if (addSavedCardQueryParams) {
    queryParams.paymentToken = reference;
    // 13 is the response code for cancellation
    queryParams.responseCode = '17';
  }

  return appendQueryParams(cancelUrl, queryParams);
};

const getLangDirection = (locale = 'en') => {
  const rtlLanguages = [
    'ae', /* Avestan */
    'ar', /* 'العربية', Arabic */
    'arc', /* Aramaic */
    'bcc', /* 'بلوچی مکرانی', Southern Balochi */
    'bqi', /* 'بختياري', Bakthiari */
    'ckb', /* 'Soranî / کوردی', Sorani */
    'dv', /* Dhivehi */
    'fa', /* 'فارسی', Persian */
    'glk', /* 'گیلکی', Gilaki */
    'he', /* 'עברית', Hebrew */
    'ku', /* 'Kurdî / كوردی', Kurdish */
    'mzn', /* 'مازِرونی', Mazanderani */
    'nqo', /* N'Ko */
    'pnb', /* 'پنجابی', Western Punjabi */
    'ps', /* 'پښتو', Pashto, */
    'sd', /* 'سنڌي', Sindhi */
    'ug', /* 'Uyghurche / ئۇيغۇرچە', Uyghur */
    'ur', /* 'اردو', Urdu */
    'yi' /* 'ייִדיש', Yiddish */
  ];
  return includes(rtlLanguages, locale.toLowerCase()) ? 'rtl' : 'ltr';
};

// eslint-disable-next-line consistent-return
const getCardScheme = (pan) => {
  if (!pan || typeof pan !== 'string' || pan.length < 1) {
    return null;
  }
  const cleanedPan = pan.replace(/\D/g, '');

  if (cleanedPan.startsWith('4')) {
    return 'VISA';
  } else if (
    /^5[1-5]/.test(cleanedPan) ||
    /^2(22[1-9]|2[3-9][0-9]|[3-6][0-9]{2}|7[0-1][0-9]|72[0-2])/.test(cleanedPan)
  ) {
    return 'MASTERCARD';
  } else if (/^3[47]/.test(cleanedPan)) {
    return 'AMERICAN_EXPRESS';
  } else if (/^6011|^65|^64[4-9]|^6[4-5][0-9]{2}/.test(cleanedPan)) {
    return 'DISCOVER';
  } else if (/^35/.test(cleanedPan)) {
    return 'JCB';
  } else if (/^30[0-5]|^3[68]|^39/.test(cleanedPan)) {
    return 'DINERS_CLUB_INTERNATIONAL';
  }
};

const formatExpiry = expiry => `20${expiry.split('/')[1].trim()}-${expiry.split('/')[0].trim()}`;

export {
  retrieveQueryParams,
  setInStorage,
  setInLocalStorage,
  getFromStorage,
  checkIfStorageIsAvailable,
  getRedirectToMerchantUrl,
  getCanceUrl,
  getLangDirection,
  getCardScheme,
  formatExpiry,
  isSavedCard
};
